import { AxiosResponse } from "axios";
import { Buffer } from "buffer";
import Attachment from "../models/Attachment";
import Api from "./Api";
import downloadFile from "../../functions/downloadFile.function";

class AttachmentApi {
  constructor(private api: Api) {
    this.api = process.env.REACT_APP_ATTACHMENT_API_SERVER
      ? api.withBaseUrl(process.env.REACT_APP_ATTACHMENT_API_SERVER)
      : api;
  }

  public uploadFile(
    payload: FormData,
    progressCallback?: (progressEvent: { loaded: number; total: number }) => void,
  ): Promise<AxiosResponse<Attachment>> {
    const file = payload.get("file");
    if (!payload.get("filename") && file && typeof file === "object" && file.name) {
      payload.append("filename", file.name.normalize());
    }
    return this.api.post("/attachment/attachment", payload, {
      onUploadProgress: progressCallback,
    });
  }

  public fetchAttachments(payload: AttachmentRaw.Schemas.AttachmentsAndAccessTokenRequest): Promise<Array<Attachment>> {
    return this.api.post("/attachment/attachment-verified/query", payload).then((response) =>
      // re-apply original ordering
      payload.attachmentIds
        .map((val) => response.data.find((attachment: Attachment) => attachment.uuid === val))
        .filter((row) => !!row),
    );
  }

  public handleDownload(
    filename: string,
    payload: AttachmentRaw.Schemas.AttachmentsAndAccessTokenRequest,
  ): Promise<void> {
    if (payload.attachmentIds.length === 0) {
      return Promise.resolve();
    }
    const query = new URLSearchParams({
      filename,
    });

    return this.api
      .post(`/attachment/download-verified?${query}`, payload, {
        responseType: "arraybuffer",
      })
      .then(({ data }) => {
        const link = document.createElement("a");
        /**
         * mime-type "image" works for all current use cases; might need adjustment if different scenario comes up
         */
        link.href = `data:image;base64,${Buffer.from(data, "binary").toString("base64")}`;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
      });
  }

  public downloadThumbnail(payload: AttachmentRaw.Schemas.AttachmentsAndAccessTokenRequest): Promise<string> {
    return this.api.post("/attachment/download-verified/thumbnails/TILE", payload).then((response) => {
      return `data:image;base64,${response.data[0].data}`;
    });
  }

  public downloadGallery(payload: AttachmentRaw.Schemas.AttachmentsAndAccessTokenRequest): Promise<string> {
    return this.api.post("/attachment/download-verified/thumbnails/GALLERY", payload).then((response) => {
      return `data:image;base64,${response.data[0].data}`;
    });
  }

  public downloadAttachmentsStructured(payload: AttachmentRaw.Schemas.AccessTokenRequest): Promise<void> {
    return this.api
      .post("/attachment/download-verified/structured", payload, {
        responseType: "blob",
      })
      .then((res) => downloadFile(res));
  }

  public updateAttachmentDescription(
    uuid: string,
    newDescription: string,
    accessToken: string,
  ): Promise<AxiosResponse<Attachment>> {
    return this.api.patch(`/attachment/attachment-verified/${uuid}`, {
      accessToken,
      newDescription,
    });
  }
}

export default AttachmentApi;
